import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Package, Warehouse, PackagePlus, MoveRight, ListChecks,
  Truck, Undo2, DoorOpen, ScanLine, Users, ShieldCheck, ScrollText, BarChart3,
  Search, Bell, Sun, Moon, Menu, X, LogOut, ChevronDown, Boxes, Command,
} from "lucide-react";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { StatusBadge } from "@/components/StatusBadge";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard.view" },
  { section: "Operations" },
  { to: "/inward", label: "Inward / GRN", icon: PackagePlus, perm: "inward.view" },
  { to: "/putaway", label: "Put Away", icon: MoveRight, perm: "putaway.view" },
  { to: "/picking", label: "Picking", icon: ListChecks, perm: "picking.view" },
  { to: "/outward", label: "Outward / Dispatch", icon: Truck, perm: "outward.view" },
  { to: "/returns", label: "Returns", icon: Undo2, perm: "returns.view" },
  { to: "/gate", label: "In / Out Register", icon: DoorOpen, perm: "gate.view" },
  { to: "/scan", label: "Scan (QR/RFID)", icon: ScanLine, perm: "scan.view" },
  { section: "Inventory" },
  { to: "/inventory", label: "Item Master", icon: Package, perm: "inventory.view" },
  { to: "/warehouses", label: "Warehouses", icon: Warehouse, perm: "warehouses.view" },
  { section: "Administration" },
  { to: "/users", label: "Users", icon: Users, perm: "users.view" },
  { to: "/roles", label: "Roles & Permissions", icon: ShieldCheck, perm: "roles.view" },
  { to: "/audit", label: "Audit Logs", icon: ScrollText, perm: "audit.view" },
  { to: "/reports", label: "Reports", icon: BarChart3, perm: "reports.view" },
];

export default function Layout({ children }) {
  const { user, logout, can } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [cmdResults, setCmdResults] = useState([]);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setCmdOpen((o) => !o); }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const loadNotifs = async () => {
    try { const { data } = await api.get("/notifications"); setNotifs(data); } catch {}
  };
  useEffect(() => { loadNotifs(); }, []);

  const runSearch = async (q) => {
    if (!q) { setCmdResults([]); return; }
    try { const { data } = await api.get("/search", { params: { q } }); setCmdResults(data.results); } catch {}
  };

  const navItems = NAV.filter((n) => n.section || can(n.perm));
  const initials = (user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-background">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-card border-r border-slate-200 dark:border-border flex flex-col transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-200 dark:border-border shrink-0">
          <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center">
            <Boxes className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-slate-900 dark:text-foreground leading-tight">StockPilot</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Warehouse OS</div>
          </div>
          <button className="ml-auto lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-close">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
          {navItems.map((n, i) =>
            n.section ? (
              <div key={i} className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">{n.section}</div>
            ) : (
              <NavLink key={n.to} to={n.to} end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400"
                             : "text-slate-600 dark:text-muted-foreground hover:bg-slate-100 dark:hover:bg-secondary hover:text-slate-900 dark:hover:text-foreground"
                  }`}>
                <n.icon className="h-[18px] w-[18px]" strokeWidth={2} />
                {n.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="p-3 border-t border-slate-200 dark:border-border text-[11px] text-slate-400">
          v1.0.0 · Enterprise WMS
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-white/90 dark:bg-card/90 backdrop-blur-md border-b border-slate-200 dark:border-border flex items-center gap-3 px-4 sm:px-6 shrink-0 sticky top-0 z-30">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="sidebar-open">
            <Menu className="h-5 w-5 text-slate-600" />
          </button>
          <button
            data-testid="command-trigger"
            onClick={() => setCmdOpen(true)}
            className="flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 dark:border-border bg-slate-50 dark:bg-secondary text-slate-400 text-sm hover:bg-slate-100 transition-colors min-w-[180px] sm:min-w-[280px]">
            <Search className="h-4 w-4" />
            <span className="flex-1 text-left">Search items, orders...</span>
            <kbd className="hidden sm:inline text-[10px] px-1.5 py-0.5 rounded border border-slate-200 dark:border-border bg-white dark:bg-card">⌘K</kbd>
          </button>

          <div className="ml-auto flex items-center gap-1.5">
            <button data-testid="theme-toggle" onClick={toggle}
              className="h-10 w-10 flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-secondary text-slate-600 dark:text-muted-foreground transition-colors">
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="notif-trigger" className="relative h-10 w-10 flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-secondary text-slate-600 dark:text-muted-foreground transition-colors">
                  <Bell className="h-5 w-5" />
                  {notifs.unread > 0 && <span className="absolute top-1.5 right-1.5 h-4 w-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">{notifs.unread}</span>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel className="flex items-center justify-between">Notifications <StatusBadge status={`${notifs.unread} new`} /></DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifs.items.length === 0 ? (
                  <div className="p-4 text-sm text-slate-400 text-center">No notifications</div>
                ) : notifs.items.slice(0, 6).map((n) => (
                  <div key={n.id} className="px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-secondary border-b border-slate-50 dark:border-border last:border-0">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={n.severity} />
                      <span className="text-sm font-medium text-slate-800 dark:text-foreground">{n.title}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{n.message}</p>
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 h-10 pl-1 pr-2 rounded-md hover:bg-slate-100 dark:hover:bg-secondary transition-colors">
                  <Avatar className="h-8 w-8"><AvatarFallback className="bg-blue-600 text-white text-xs font-semibold">{initials}</AvatarFallback></Avatar>
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-medium text-slate-800 dark:text-foreground leading-none">{user?.name}</div>
                    <div className="text-[11px] text-slate-400 capitalize">{user?.role?.replace("_", " ")}</div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-400 hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">Profile & Settings</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">{children}</main>
      </div>

      <CommandDialog open={cmdOpen} onOpenChange={setCmdOpen}>
        <CommandInput placeholder="Search or jump to..." onValueChange={runSearch} data-testid="command-input" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          {cmdResults.length > 0 && (
            <CommandGroup heading="Results">
              {cmdResults.map((r) => (
                <CommandItem key={`${r.type}-${r.id}`} onSelect={() => {
                  setCmdOpen(false);
                  navigate(r.type === "item" ? "/inventory" : r.type === "warehouse" ? "/warehouses" : "/inward");
                }}>
                  <span className="capitalize text-xs text-slate-400 mr-2">{r.type}</span>
                  {r.title} <span className="ml-2 text-xs text-slate-400 font-mono">{r.sub}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          <CommandGroup heading="Navigate">
            {navItems.filter((n) => !n.section).map((n) => (
              <CommandItem key={n.to} onSelect={() => { setCmdOpen(false); navigate(n.to); }}>
                <n.icon className="h-4 w-4 mr-2 text-slate-400" />{n.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </div>
  );
}
